import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { Check, Search } from 'lucide-react'
import { findTypeaheadIndex, isTypeaheadKey, renderOptionIcon, scanOptions, useTypeahead } from './listboxTypeahead'
import styles from './ListboxPicker.module.css'

// How long typing has to pause before the number of matches is announced.
const ANNOUNCE_DELAY = 500

/**
 * A list of options to pick from, with an optional search box above it: an
 * ARIA listbox whose options can sit in labelled groups. Focus stays in the
 * search box (or on the list without one) and aria-activedescendant follows
 * the highlighted option: arrow keys move it (wrapping), Enter picks it, and
 * Space picks it too while there is nothing typed. `multiple` draws a check
 * box on each option and marks the list multi-selectable; picking calls
 * `onPick(option)` either way, so the caller decides what a pick does.
 *
 * Options are `{ value, label, hint?, count?, group?, muted?, disabled?, icon? }`:
 * `hint` is a second line ("Formula 1"), `count` a number at the end, `group`
 * the heading of the run of options it belongs to (in the order the options
 * come), `icon` a leading element or icon component. A `disabled` option is
 * shown but skipped by the keys and can't be picked.
 *
 * `initialActiveValue` is the option highlighted (and scrolled to) when the
 * list mounts; without it, the first one. Without a search box, typing
 * letters jumps to the option they start (`text`, else a string `label`);
 * `typeahead` (from useTypeahead) shares the letters typed so far with the
 * host, so a search started on a SelectMenu button carries on in the list.
 *
 * `listId` sets the listbox's id, for a host's aria-controls. With a search
 * box, the number of matches is announced once typing settles.
 */
export function ListboxPicker({
  label,
  options,
  isSelected = () => false,
  onPick,
  multiple = false,
  searchable = true,
  query = '',
  onQueryChange,
  searchLabel,
  searchPlaceholder,
  emptyMessage = 'No matches',
  initialActiveValue,
  listId: listIdProp,
  typeahead: sharedTypeahead,
}) {
  const id = useId()
  const listId = listIdProp || `${id}-list`
  const optionId = (index) => `${id}-option-${index}`
  const listRef = useRef(null)
  const ownTypeahead = useTypeahead()
  const typeahead = sharedTypeahead || ownTypeahead
  // The first enabled option from `start`, stepping by `step` and wrapping.
  const enabledFrom = (start, step) => scanOptions(options, start, step, (option) => !option.disabled)
  const [activeIndex, setActiveIndex] = useState(() => {
    const initial = initialActiveValue === undefined
      ? -1
      : options.findIndex((option) => option.value === initialActiveValue && !option.disabled)
    return initial >= 0 ? initial : enabledFrom(0, 1)
  })

  // A new query or list starts from the first option, in the same render,
  // so aria-activedescendant never names an option that is gone and Enter
  // never picks one the operator can no longer see.
  const listKey = `${query}\u0000${options.length}`
  const [seenListKey, setSeenListKey] = useState(listKey)
  if (seenListKey !== listKey) {
    setSeenListKey(listKey)
    setActiveIndex(enabledFrom(0, 1))
  }
  // ...and a new query scrolls back to it.
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0
  }, [query])
  // The option highlighted on mount sits in the middle of the list. Set on the
  // list itself: scrollIntoView could scroll the page under a popover that is
  // not placed yet.
  useLayoutEffect(() => {
    const list = listRef.current
    const option = activeIndex >= 0 ? document.getElementById(optionId(activeIndex)) : null
    if (initialActiveValue === undefined || !list || !option) return
    const offset = option.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop
    list.scrollTop = Math.max(0, offset - (list.clientHeight - option.offsetHeight) / 2)
  // Mount only: later moves scroll themselves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // What a filter found, announced once typing pauses rather than per key.
  const resultsMessage = options.length
    ? `${options.length} ${options.length === 1 ? 'result' : 'results'}`
    : emptyMessage
  const [announcement, setAnnouncement] = useState('')
  const announcements = useRef(0)
  useEffect(() => {
    if (!searchable) return undefined
    // A trailing no-break space on every other announcement makes the text
    // change, so "3 results" is read again for a new query with the same count.
    const timer = setTimeout(() => {
      announcements.current += 1
      setAnnouncement(query === '' ? '' : `${resultsMessage}${announcements.current % 2 ? '\u00a0' : ''}`)
    }, ANNOUNCE_DELAY)
    return () => clearTimeout(timer)
  }, [searchable, query, resultsMessage])

  const highlight = (index) => {
    if (index < 0) return
    setActiveIndex(index)
    document.getElementById(optionId(index))?.scrollIntoView?.({ block: 'nearest' })
  }
  // Steps over disabled options.
  const move = (next, step) => {
    if (!options.length) return
    highlight(enabledFrom(next, step))
  }
  const pick = (option) => {
    if (option && !option.disabled) onPick(option)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      move(activeIndex + step, step)
      return
    }
    if (!searchable && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault()
      if (event.key === 'Home') move(0, 1)
      else move(options.length - 1, -1)
      return
    }
    if (!searchable && isTypeaheadKey(event)) {
      event.preventDefault()
      highlight(findTypeaheadIndex(options, typeahead(event.key), activeIndex))
      return
    }
    // Space types into a search; with nothing typed it picks, as in a list.
    const pickKey = event.key === 'Enter' || (event.key === ' ' && (!searchable || query === ''))
    if (pickKey) {
      event.preventDefault()
      pick(options[activeIndex])
    }
  }

  const listProps = {
    ref: listRef,
    id: listId,
    role: 'listbox',
    'aria-label': label,
    'aria-multiselectable': multiple || undefined,
    className: styles.list,
  }
  // Without a search box the list itself takes focus and the keys.
  const focusProps = searchable
    ? {}
    : {
      tabIndex: 0,
      'data-autofocus': '',
      'aria-activedescendant': activeIndex >= 0 ? optionId(activeIndex) : undefined,
      onKeyDown: handleKeyDown,
    }

  const groups = []
  options.forEach((option, index) => {
    const name = option.group || ''
    const last = groups.at(-1)
    if (last && last.name === name) last.items.push({ option, index })
    else groups.push({ name, items: [{ option, index }] })
  })

  const renderOption = ({ option, index }) => {
    const selected = Boolean(isSelected(option.value))
    const classes = [
      styles.option,
      index === activeIndex ? styles.optionActive : '',
      selected ? styles.optionSelected : '',
      option.muted ? styles.optionMuted : '',
      option.disabled ? styles.optionDisabled : '',
    ].filter(Boolean).join(' ')
    const icon = renderOptionIcon(option.icon)
    return (
      <div
        key={option.value}
        id={optionId(index)}
        role="option"
        aria-selected={selected}
        aria-disabled={option.disabled || undefined}
        className={classes}
        // The search box keeps focus: a click picks without moving it.
        // Hovering an enabled option highlights it, as the arrow keys do, so
        // Enter picks what the pointer is on.
        onMouseDown={(event) => event.preventDefault()}
        onMouseEnter={option.disabled ? undefined : () => setActiveIndex(index)}
        onClick={() => pick(option)}
      >
        {multiple ? (
          <span className={styles.box} aria-hidden="true">{selected ? <Check size={12} strokeWidth={3} /> : null}</span>
        ) : null}
        {icon ? <span className={styles.icon} aria-hidden="true">{icon}</span> : null}
        <span className={styles.text}>
          <span className={styles.label}>{option.label}</span>
          {option.hint ? <span className={styles.hint}>{option.hint}</span> : null}
        </span>
        {option.count != null ? <span className={styles.count}>{option.count}</span> : null}
        {!multiple && selected ? <Check size={16} className={styles.check} aria-hidden="true" /> : null}
      </div>
    )
  }

  return (
    <div className={styles.picker}>
      {searchable ? (
        <div className={styles.search}>
          <Search size={16} className={styles.searchIcon} aria-hidden="true" />
          <input
            type="text"
            className={styles.searchInput}
            value={query}
            onChange={(event) => onQueryChange?.(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchLabel || `Search ${label}`}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            autoComplete="off"
            spellCheck={false}
            data-autofocus=""
          />
        </div>
      ) : null}
      <div {...listProps} {...focusProps}>
        {options.length === 0 ? (
          <div className={styles.empty} role="presentation">{emptyMessage}</div>
        ) : groups.map((group, groupIndex) => (group.name ? (
          <div key={`${group.name}-${groupIndex}`} role="group" aria-labelledby={`${id}-group-${groupIndex}`} className={styles.group}>
            <div id={`${id}-group-${groupIndex}`} role="presentation" className={styles.groupLabel}>{group.name}</div>
            {group.items.map(renderOption)}
          </div>
        ) : group.items.map(renderOption)))}
      </div>
      {searchable ? <p role="status" className="wf-visually-hidden">{announcement}</p> : null}
    </div>
  )
}
